import { ContributorAvatar } from '@/components/contributor/avatar';
import { useSessionAudio } from '@/hooks/use-program-card-audio';
import { MediaProgramMetadata } from '@/utils/media/types';
import { RbcCircleRec } from '@calm/evexia-web-sdk';
import { styleClinicalBadgeIcon } from '@rhg/components/program-card';
import { useHumanReadable } from '@rhg/hooks/use-human-readable';
import useIsContentLocked from '@rhg/hooks/use-is-content-locked';
import { useProvidedProgramContextWithWorkflow } from '@rhg/hooks/use-provided-program-context/with-workflow';
import { useTrackPackItemViewedEvent } from '@rhg/hooks/use-track-pack-item-viewed-event';
import commonMessages from '@rhg/lang/messages/common';
import { CalmClinicalBadge } from '@rhg/libs/material/icons';
import { MediaType, getMediaType, getGuideDuration } from '@rhg/utils/media';
import { getProgramRoute } from '@rhg/utils/programs';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ProgramContextAwareSessionCardProps } from '..';
import { useMindshiftSessionCardContext } from '../use-mindshift-session-card-context';

type UseVariantAgnosticSessionCardProps = Pick<
    ProgramContextAwareSessionCardProps,
    | 'session'
    | 'categoryTitle'
    | 'sessionIndex'
    | 'trackProps'
    | 'showNarratorAvatar'
    | 'disableViewedTracking'
>;
/**
 * Do not use this hook outside of SessionCard and its dependencies.
 */
export function useVariantAgnosticSessionCardProps({
    session,
    categoryTitle: _categoryTitle,
    trackProps,
    showNarratorAvatar,
    disableViewedTracking,
}: UseVariantAgnosticSessionCardProps) {
    const { formatMessage } = useIntl();
    const { duration } = useHumanReadable();
    const { getIsContentLocked } = useIsContentLocked();

    const {
        program: _program,
        programMetadata,
        packClass,
        programType,
        programState,
    } = useProvidedProgramContextWithWorkflow();

    const program: (MediaProgramMetadata & Partial<RbcCircleRec>) | undefined =
        _program ?? programMetadata;

    const { isMindshiftSession, mindshiftCategoryTitle } =
        useMindshiftSessionCardContext(session, packClass);

    const { isPlaying } = useSessionAudio({
        // True because we only want to check if the session matches the audio and is in progress
        inProgressDTC: true,
        currentSession: session,
    });

    const isLocked = getIsContentLocked({
        programState,
        programType,
    });

    const isVideo = getMediaType(session) === MediaType.Video;
    const isClinicalSession = !!program?.isClinical;
    const isDailySession = Boolean(session.dailyContentType);
    const sessionDuration = getGuideDuration(session);
    const programPushRoute = isClinicalSession
        ? getProgramRoute(program, packClass)
        : undefined;

    const alreadyShowingProgramTitle =
        // dailies and mindshifts show program name in the category section
        isDailySession ||
        isMindshiftSession ||
        // if the session name is the same as the program name, then the program name is in the title section already
        program?.title === session.title;

    const subtitle = alreadyShowingProgramTitle
        ? program?.narrator?.name
        : program?.title;

    const label = formatMessage(commonMessages['common.play'], {
        title: session?.title,
    });

    const durationText =
        session?.meditationType === 'soundscape'
            ? undefined
            : duration(sessionDuration);

    const categoryTitle = useMemo(() => {
        if (_categoryTitle) {
            return _categoryTitle;
        }
        if (isMindshiftSession) {
            return mindshiftCategoryTitle;
        }

        return isClinicalSession
            ? formatMessage(
                  commonMessages['common.program_type.clinical_program.title']
              )
            : session?.contentType;
    }, [
        _categoryTitle,
        formatMessage,
        isClinicalSession,
        isMindshiftSession,
        mindshiftCategoryTitle,
        session?.contentType,
    ]);

    const categoryIcon = useMemo(
        () =>
            isClinicalSession ? (
                <CalmClinicalBadge sx={styleClinicalBadgeIcon} />
            ) : undefined,
        [isClinicalSession]
    );

    // Visibility tracking for "Pack: Item: Viewed" analytics event
    const cardRef = useTrackPackItemViewedEvent({
        guide: session,
        program,
        trackProps,
        disabled: disableViewedTracking,
    });

    const titleIcon =
        showNarratorAvatar && program?.narrator ? (
            <ContributorAvatar contributor={program.narrator} size="medium" />
        ) : undefined;

    return {
        isVideo,
        categoryIcon,
        categoryTitle,
        subtitle,
        isLocked,
        durationText,
        program,
        label,
        isPlaying,
        programPushRoute,
        isMindshiftSession,
        isClinicalSession,
        isDailySession,
        cardRef,
        titleIcon,
    };
}
